import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@auth0/nextjs-auth0/client';
import { initAll, track, identify, Identify } from '@amplitude/unified';
import { createLog } from 'services/logService';

const AmplitudeContext = createContext(null);

export function AmplitudeProvider({ children }) {
  const { user } = useUser();
  const amplitudeInitialized = useRef(false);
  const engagementLoaded = useRef(false);
  const engagementBooted = useRef(false);
  const engagementRef = useRef(null);

  const API_KEY =
    process.env.NEXT_PUBLIC_AMPLITUDE_API || process.env.AMPLITUDE_API_KEY;
  const IS_PROD = JSON.parse(
    process.env.NEXT_PUBLIC_PRODUCTION ?? process.env.PRODUCTION ?? 'false'
  );

  useEffect(() => {
    if (amplitudeInitialized.current) return;
    if (!API_KEY || !IS_PROD) {
      amplitudeInitialized.current = true;
      return;
    }
    try {
      initAll(API_KEY);
    } catch (e) {
      createLog('Amplitude (Unified) init failed', e);
    } finally {
      amplitudeInitialized.current = true;
    }
  }, [API_KEY, IS_PROD]);

  const bootGuides = useCallback(async (user) => {
    try {
      if (typeof window === 'undefined') return;
      if (!engagementLoaded.current || !engagementRef.current) return;
      if (engagementBooted.current) return;

      if (user && user.user_properties) {
        const id = new Identify();
        Object.entries(user.user_properties).forEach(([k, v]) => id.set(k, v));
        identify(id);
      }

      await engagementRef.current.boot({
        user: {
          user_id: user && user?.email,
        },
        integrations: [
          {
            track: (evt) => {
              track(evt.event_type, evt.event_properties);
            },
          },
        ],
      });

      engagementBooted.current = true;
    } catch (e) {
      createLog('Engagement boot failed', e);
    }
  }, []);

  const shutdownGuides = useCallback(() => {
    try {
      if (engagementRef.current && engagementBooted.current) {
        engagementRef.current.shutdown();
        engagementBooted.current = false;
      }
    } catch (e) {
      createLog('Engagement shutdown failed', e);
    }
  }, []);

  const showGuide = useCallback((idOrSlug) => {
    try {
      if (engagementRef?.current?.show) {
        engagementRef.current.show(idOrSlug);
      }
    } catch (e) {
      createLog('Engagement show failed', e);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (engagementLoaded.current) return;
      if (typeof window === 'undefined') return;
      if (!API_KEY || !IS_PROD) return;

      try {
        const mod = await import('@amplitude/engagement-browser');

        if (cancelled) return;

        mod.init(API_KEY);

        engagementRef.current = window.engagement || null;
        engagementLoaded.current = true;

        await bootGuides(user);
      } catch (e) {
        createLog('Amplitude (Engagement) load/init failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_KEY, IS_PROD, user, bootGuides]);

  const trackAmplitudeEvent = useCallback((eventName, eventProps) => {
    try {
      track(eventName, eventProps);
    } catch (e) {
      createLog('Amplitude track failed', e);
    }
  }, []);

  const value = useMemo(
    () => ({
      trackAmplitudeEvent,
      bootGuides,
      shutdownGuides,
      showGuide,
    }),
    [trackAmplitudeEvent, bootGuides, shutdownGuides, showGuide]
  );

  return (
    <AmplitudeContext.Provider value={value}>
      {children}
    </AmplitudeContext.Provider>
  );
}

AmplitudeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAmplitude = () => {
  const context = useContext(AmplitudeContext);

  if (!context) {
    throw new Error('useAmplitude must be used within an AmplitudeProvider');
  }

  return context;
};
